import { JRCMessage } from '../../JRCMessage/JRCMessage';
import { JRCHtml } from '../../JRCHtml/JRCHtml';
import { JRCAvatar } from '../../JRCAvatar/JRCAvatar';
import { JRCInputTinyMCERaw, JRCInputTinyMCERawProps } from '../../Form/Input/JRCInputTinyMCERaw/JRCInputTinyMCERaw';
import { StyledInput } from '../../Form/Input/JRCStyledInput';
import {
    DriveOrFilebank,
    JRCInputFileAdvancedRaw,
} from '../../Form/Input/JRCInputFileAdvanced/JRCInputFileAdvancedRaw';
import { JRCTooltip } from '../../JRCTooltip/JRCTooltip';
import { JRCIconButton } from '../../JRCIconButton/JRCIconButton';
import { JRCButton } from '../../JRCButton/JRCButton';
import React, { useCallback, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { AddCommentParams, JFileApi, JFilebankApi, jFileLittle, jUserList } from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { TinyMCEExtension } from '../../Form/Input/JRCInputTinyMCERaw/extensions/JTinyMCEExtensions';

const NewCommentContainer = styled.div`
    display: flex;
    gap: 12px;
    padding: 12px 12px 8px 24px;
`;

const NewCommentContainerContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 14px;
`;

const TinyMCEWrapper = styled.div`
    width: 100%;
`;

const NewCommentInputWrapper = styled.div`
    width: 100%;
`;

const ActionsContainer = styled.div`
    display: flex;
    justify-content: right;
    padding: 0 12px 12px 12px;
    gap: 8px;
`;

const AttachmentsWrapper = styled.div`
    margin-top: 8px;
`;

export type NewCommentProps = {
    currentUser: jUserList;
    widgetListComponent: React.ReactNode;
    newWidgetsWrapperComponent: React.ReactNode;
    loading?: boolean;
    canComment: boolean;
    onComment: (params: Pick<AddCommentParams, 'content' | 'alertAuthor' | 'sendAlert'>) => Promise<void>;
    token?: string;
    tinyMCEConfig: Pick<JRCInputTinyMCERawProps, 'commonOptions' | 'mentionsQueries' | 'tinymceScriptSrc'>;
    handlers?: {
        filebank: Pick<JFilebankApi, 'getBanks' | 'getFolders' | 'getDocuments'>;
        file: Pick<JFileApi, 'copy'>;
        drive: Pick<
            JDriveApi,
            | 'msDocuments'
            | 'dropboxDocuments'
            | 'groups'
            | 'tenants'
            | 'listAccount'
            | 'isAccountLogged'
            | 'checkAccountToken'
            | 'getAccount'
            | 'logout'
            | 'setDefaultAccount'
            | 'getDocument'
        >;
    };
    onError?: (message?: string) => void;
    activeDrives: DriveOrFilebank[];
    canCreateWidget?: boolean;
    socialQuestion?: boolean;
    deleteFile?: (id: number) => Promise<void>;
    additionalExtensions: Array<TinyMCEExtension>;
};

export const NewComment = ({
    newWidgetsWrapperComponent,
    widgetListComponent,
    canComment,
    loading,
    currentUser,
    onComment,
    onError,
    tinyMCEConfig,
    token,
    handlers,
    activeDrives,
    canCreateWidget,
    socialQuestion,
    deleteFile,
    additionalExtensions,
}: NewCommentProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const [isTextEditorVisible, setTextEditorVisible] = useState(false);
    const [comment, setComment] = useState('');
    const [isMailNotificationActivated, setMailNotificationActivated] = useState(true);
    const [isCopyForUserActivated, setCopyForUserActivated] = useState(false);
    const [isAttachmentsOpen, setAttachmentsOpen] = useState(false);
    const [attachments, setAttachments] = useState<jFileLittle[]>([]);
    const [isAttachmentsActive, setAttachmentsActive] = useState(false);

    const doOnComment = useCallback(() => {
        onComment({
            content: comment,
            alertAuthor: isCopyForUserActivated,
            sendAlert: isMailNotificationActivated,
        }).then(() => {
            setTextEditorVisible(false);
            setAttachmentsOpen(false);
            setComment('');
            setAttachments([]);
            setAttachmentsActive(false);
        });
    }, [comment, isCopyForUserActivated, isMailNotificationActivated, onComment]);

    return (
        <>
            <NewCommentContainer>
                {!canComment ? (
                    <JRCMessage
                        variant={'info'}
                        message={<JRCHtml __html={intl.formatMessage({ id: 'ACTIVITY_Can_Not_comment_Desc' })} />}
                    />
                ) : (
                    <>
                        <JRCAvatar uri={currentUser.uri} size={5} variant="circle" />
                        <NewCommentContainerContent>
                            <NewCommentInputWrapper>
                                {isTextEditorVisible ? (
                                    <TinyMCEWrapper>
                                        <JRCInputTinyMCERaw
                                            {...tinyMCEConfig}
                                            token={token}
                                            onChange={setComment}
                                            value={comment}
                                            height={250}
                                            autofocus
                                            dataCy={'new-comment-tiny'}
                                            additionalExtensions={additionalExtensions}
                                        />
                                    </TinyMCEWrapper>
                                ) : (
                                    <div onClick={() => setTextEditorVisible(true)} data-cy="new-comment-input">
                                        <StyledInput
                                            style={{ backgroundColor: theme.color.white }}
                                            placeholder={intl.formatMessage({ id: 'COMMENT_Placeholder' })}
                                        />
                                    </div>
                                )}
                                {isAttachmentsActive && (
                                    <AttachmentsWrapper>
                                        <JRCInputFileAdvancedRaw
                                            deleteFile={deleteFile}
                                            onUploadSuccess={(f) => {
                                                setAttachments([...attachments, f]);
                                                if (!comment) {
                                                    setComment(f.title);
                                                }
                                            }}
                                            onDeleteSuccess={(f) =>
                                                setAttachments(attachments.filter((_, i) => f !== i))
                                            }
                                            token={token}
                                            activeDrives={activeDrives}
                                            multiple
                                            handlers={handlers}
                                            dispatchOnError={onError}
                                            directFilePopupOpening={{
                                                open: isAttachmentsOpen,
                                                onClose: () => setAttachmentsOpen(false),
                                            }}
                                        />
                                    </AttachmentsWrapper>
                                )}
                            </NewCommentInputWrapper>

                            {canCreateWidget && newWidgetsWrapperComponent}
                        </NewCommentContainerContent>
                    </>
                )}
            </NewCommentContainer>
            {canComment && (
                <ActionsContainer>
                    <JRCTooltip description={intl.formatMessage({ id: 'SPOTLIGHT_Send_Alert_To_Subscribers' })}>
                        <JRCIconButton
                            bg={isMailNotificationActivated ? 'green' : 'grey1'}
                            bgHover={isMailNotificationActivated ? 'greenL20' : 'grey2'}
                            color={isMailNotificationActivated ? 'white' : 'grey5'}
                            iconSize={14}
                            onClick={() => setMailNotificationActivated(!isMailNotificationActivated)}
                            icon={'icon-envelope'}
                            dataCy={'comment-email-notif'}
                        />
                    </JRCTooltip>
                    <JRCTooltip description={intl.formatMessage({ id: 'SPOTIT_Receive_A_Copy' })}>
                        <JRCIconButton
                            bg={isCopyForUserActivated ? 'green' : 'grey1'}
                            bgHover={isCopyForUserActivated ? 'greenL20' : 'grey2'}
                            color={isCopyForUserActivated ? 'white' : 'grey5'}
                            iconSize={14}
                            onClick={() => setCopyForUserActivated(!isCopyForUserActivated)}
                            icon={'icon-user'}
                            dataCy={'comment-receive-copy'}
                        />
                    </JRCTooltip>
                    {canCreateWidget && widgetListComponent}
                    <JRCTooltip description={intl.formatMessage({ id: 'GLOBAL_Add_File' })}>
                        <JRCIconButton
                            bg={'grey1'}
                            bgHover={'grey2'}
                            color={'grey5'}
                            iconSize={14}
                            onClick={() => {
                                setAttachmentsActive(true);
                                setTextEditorVisible(true);
                                setAttachmentsOpen(true);
                            }}
                            icon={'icon-attach'}
                            dataCy={'comment-add-file'}
                        />
                    </JRCTooltip>
                    <JRCButton
                        data-cy="add-comment"
                        noMargin
                        color={'primary'}
                        disabled={!comment || !token || loading}
                        onClick={doOnComment}>
                        {intl.formatMessage({
                            id: socialQuestion ? 'COMMENT_Answer' : 'COMMENT_Add_Comment',
                        })}
                    </JRCButton>
                </ActionsContainer>
            )}
        </>
    );
};
